import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { differenceWith, intersectionWith } from 'lodash';
import styled from 'styled-components';
import Config from 'Config';
import Alert from 'common/components/Alert';
import FormSelect from 'common/components/form/FormSelect';
import FormTextView from 'common/components/form/FormTextView';
import Drawer from 'common/components-v2/modal/Drawer';
import MotiliButton from 'common/components-v2/buttons/MotiliButton';
import * as logger from 'common/utils/Logger';
import { useWorkOrderPhotoTypesQuery } from 'common/stores/redux/MetaStore/MetaStoreApi';
import { Checkbox } from '@motili/ui';
import { FormElement, FormRow } from 'common/components/form/FormLayout';
import {
    PreviewContainer,
    PDFPreview,
    OtherPreview,
} from 'common/components/FilePreview';
import { isPhotoRequirementMet } from 'common/components/document/AdminDocumentRquirementsPhotos/AdminDocumentRequirementsPhotos';
import { isVideoMimeType } from 'common/components/file-upload/FileUploadTypes';
import { VideoMask } from 'common/components/document/DocumentRequirementsPhotos/DocumentRequirementsPhotosUploadedRow/WorkOrderRequirementsPhotosUploadedRow';

const DrawerBody = styled.div`
    background: #fff;
    padding-top: 8px;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: 240px 1fr;
    gap: 16px;
    align-items: start;
    margin-top: -8px;
`;

const PreviewPane = styled.div``;

const PreviewBox = styled.div`
    width: 100%;
    aspect-ratio: 1 / 1;
    max-height: 240px;
    border-radius: 2px;
    overflow: hidden;
    background: #9b9b9b;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const PreviewContent = styled.div`
    width: 100%;
    height: 100%;
    background: #000;
    display: flex;
    align-items: center;
    justify-content: center;

    color: ${Config.color.motiliLightBlue};

    i.fa {
        color: ${Config.color.motiliLightBlue};
    }

    img,
    video {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
    }
`;

const PreviewPlaceholder = styled.div`
    width: 100%;
    height: 100%;
    background: #9b9b9b;
`;

const FormPane = styled.div`
    min-width: 0;
    position: relative;
    min-height: 360px;
    padding-bottom: 56px;
`;

const ButtonsRow = styled.div`
    position: absolute;
    bottom: 0;
    right: 0;
    display: flex;
    gap: 12px;

    button {
        width: 84px;
    }
`;

const WarningWrap = styled.div`
    margin-top: 12px;
    color: ${Config.color.red};
    font-size: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const VideoPreviewContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;

function EditPhotoRequirementsModal({
    photos,
    selectedPhoto,
    currentPhotoTypeId,
    photoTypeRequirements,
    quotePhotoTypeRequirements,
    handleClose,
    handleEditPhotoRequirements,
    handleEditUploadedPhotoRequirement,
    mimeType,
    currentRequiresPullAndClean,
    renderImage,
    videoDuration,
}) {
    const { data: workOrderPhotoTypes } = useWorkOrderPhotoTypesQuery();
    const photoTypes = workOrderPhotoTypes?.filter(photoType => {
        if (mimeType === 'application/pdf') {
            return photoType.pdfSupported;
        }
        return true;
    });

    const [previousPhotoRequirements, setPreviousPhotoRequirements] = useState(
        []
    );
    const [editPhotoRequirements, setEditPhotoRequirements] = useState([]);
    const [busy, setBusy] = useState(false);
    const [requiresPullAndClean, setRequiresPullAndClean] = useState(false);
    const [
        requiresPullAndCleanBeforeUpdate,
        setRequiresPullAndCleanBeforeUpdate,
    ] = useState(false);
    const [selectedTypeId, setSelectedTypeId] = useState();
    const [selectedTypeIdBeforeUpdate, setSelectedTypeIdBeforeUpdate] =
        useState();
    const [editingSingleRequirement, setEditingSingleRequirement] =
        useState(false);
    const photoRequirementMet = photoType => {
        return isPhotoRequirementMet(photoType, photos);
    };

    const transformRequirementForDisplay = rawRequirement => {
        if (!rawRequirement.requirement) {
            return null;
        }
        const foundPhotoType = photoTypes?.find(
            photoType => photoType.id === rawRequirement.requirement.id
        );
        const reqType = rawRequirement.clientId ? 'client' : 'project';
        const isFixed = photoRequirementMet(rawRequirement.requirement.id);
        return { ...foundPhotoType, reqType, isFixed };
    };

    useEffect(() => {
        const photoRequirementsTransformed = photoTypeRequirements
            .map(transformRequirementForDisplay)
            .filter(Boolean);
        setEditPhotoRequirements(photoRequirementsTransformed);

        if (selectedPhoto && currentPhotoTypeId) {
            setEditingSingleRequirement(true);
            setSelectedTypeId(currentPhotoTypeId);
            setSelectedTypeIdBeforeUpdate(currentPhotoTypeId);
            setRequiresPullAndClean(currentRequiresPullAndClean);
            setRequiresPullAndCleanBeforeUpdate(currentRequiresPullAndClean);
        } else {
            setPreviousPhotoRequirements(photoRequirementsTransformed);
        }
    }, []);

    const sortByIsFixed = (a, b) => {
        const aIsFixed = a.isFixed;
        const bIsFixed = b.isFixed;
        return aIsFixed > bIsFixed ? -1 : aIsFixed < bIsFixed ? 1 : 0;
    };

    const photoRequirementsDisplays = requirements => {
        const displays = requirements.map(requirement => requirement.display);
        return displays.length ? displays.sort().join(', ') : '<NONE>';
    };

    const byDisplay = (req1, req2) => req1.display === req2.display;

    const addedRequirements = () => {
        const added = differenceWith(
            editPhotoRequirements,
            previousPhotoRequirements,
            byDisplay
        );
        return photoRequirementsDisplays(added);
    };

    const removedRequirements = () => {
        const removed = differenceWith(
            previousPhotoRequirements,
            editPhotoRequirements,
            byDisplay
        );
        return photoRequirementsDisplays(removed);
    };

    const editPhotoRequirementsIsFixedFirst = () => {
        return [...editPhotoRequirements].sort(sortByIsFixed);
    };

    const photoTypesFilter = () => {
        const ignorePhotoTypes = ['OTHER', 'LIFE_HEALTH_SAFETY_RISK'];
        let dropDownItems = photoTypes?.filter(photoType => {
            const existingRequirement = editPhotoRequirements.find(
                photoRequirement =>
                    photoRequirement.display === photoType.display
            );
            // CS-2624 : Only these 2 type : [DAMAGED_AREA and REPAIRED_AREA] should available in option when user not save the remove required photo.
            return (
                !existingRequirement &&
                (!photoType.deprecated ||
                    (photoTypeRequirements.find(
                        p => p?.requirement?.id === photoType.id
                    ) &&
                        ['DAMAGED_AREA', 'REPAIRED_AREA'].includes(
                            photoType.id
                        ))) &&
                !ignorePhotoTypes.includes(photoType.id)
            );
        });
        // CS-2624
        if (
            photos.some(p => p?.requirement?.photoTypeId === 'DAMAGED_AREA') ||
            photoTypeRequirements.some(
                p => p?.requirement?.id === 'DAMAGED_AREA'
            )
        ) {
            dropDownItems = dropDownItems.filter(
                i => i.id !== 'AREA_PART_NEEDING_REPAIR'
            );
        } else {
            dropDownItems = dropDownItems.filter(i => i.id !== 'DAMAGED_AREA');
        }
        if (
            photos.some(p => p?.requirement?.photoTypeId === 'REPAIRED_AREA') ||
            photoTypeRequirements.some(
                p => p?.requirement?.id === 'REPAIRED_AREA'
            )
        ) {
            dropDownItems = dropDownItems.filter(
                i => i.id !== 'AREA_PART_REPAIRED'
            );
        } else {
            dropDownItems = dropDownItems.filter(i => i.id !== 'REPAIRED_AREA');
        }
        return dropDownItems;
    };

    const handleAddMultipleFilter = values => {
        const remainingRequirements = intersectionWith(
            editPhotoRequirements,
            values,
            byDisplay
        );
        let additionalValues = differenceWith(
            values,
            editPhotoRequirements,
            byDisplay
        );

        if (additionalValues.length) {
            additionalValues = additionalValues.map(addValue => {
                const existingRequirement = previousPhotoRequirements.find(
                    previousRequirement =>
                        previousRequirement.display === addValue.display
                );
                return (
                    existingRequirement || { ...addValue, reqType: 'userAdded' }
                );
            });
        }

        const updatedRemovableRequirements = [
            ...remainingRequirements,
            ...additionalValues,
        ].filter(requirement => !requirement.isFixed);
        const nonRemovableRequirements = editPhotoRequirements.filter(
            requirement => requirement.isFixed
        );
        const updatedRequirements = [
            ...nonRemovableRequirements,
            ...updatedRemovableRequirements,
        ];
        setEditPhotoRequirements(updatedRequirements);
    };

    const singleRequirementChanged = () => {
        let isTypeChange = false;
        if (editingSingleRequirement) {
            isTypeChange = selectedTypeId !== selectedTypeIdBeforeUpdate;
            if (
                ['BLOWER_WHEEL_BEFORE', 'INDOOR_COIL_BEFORE'].includes(
                    selectedTypeId
                )
            ) {
                return (
                    isTypeChange ||
                    requiresPullAndClean !== requiresPullAndCleanBeforeUpdate
                );
            }
            return isTypeChange;
        }
        return isTypeChange;
    };
    const requirementsChanged = () => {
        const removedRequirements = differenceWith(
            previousPhotoRequirements,
            editPhotoRequirements,
            byDisplay
        );
        const addedRequirements = differenceWith(
            editPhotoRequirements,
            previousPhotoRequirements,
            byDisplay
        );
        const changedRequirements = [
            ...removedRequirements,
            ...addedRequirements,
        ];
        return !!changedRequirements.length;
    };

    const handleTypeOnChange = event => {
        setRequiresPullAndClean(false);
        setSelectedTypeId(event.id);
    };

    const singleRequirementDropdownOptions = () => {
        let options;
        const masterRequirements =
            quotePhotoTypeRequirements || editPhotoRequirements;
        if (mimeType && mimeType === 'application/pdf') {
            options = photoTypes;
        } else {
            options = photoTypes?.filter(
                type =>
                    type.id === currentPhotoTypeId ||
                    !type.deprecated ||
                    masterRequirements.some(
                        req =>
                            req.requirement?.photoTypeId === type.id ||
                            req.requirement?.id === type.id
                    )
            );
        }
        // CS-2624
        if (
            masterRequirements.some(
                req =>
                    req.requirement &&
                    (req.requirement.photoTypeId === 'DAMAGED_AREA' ||
                        req.requirement.id === 'DAMAGED_AREA')
            )
        ) {
            options = options.filter(
                option => option.id !== 'AREA_PART_NEEDING_REPAIR'
            );
        } else {
            options = options.filter(option => option.id !== 'DAMAGED_AREA');
        }
        if (
            masterRequirements.some(
                req =>
                    req.requirement &&
                    (req.requirement.photoTypeId === 'REPAIRED_AREA' ||
                        req.requirement.id === 'REPAIRED_AREA')
            )
        ) {
            options = options.filter(
                option => option.id !== 'AREA_PART_REPAIRED'
            );
        } else {
            options = options.filter(option => option.id !== 'REPAIRED_AREA');
        }
        return options.filter(option =>
            currentPhotoTypeId &&
            currentPhotoTypeId === 'LIFE_HEALTH_SAFETY_RISK'
                ? true
                : option.id !== 'LIFE_HEALTH_SAFETY_RISK'
        );
    };

    // OC-2049
    const _handleEditPhotoRequirements = async editPhotoRequirements => {
        if (
            handleEditPhotoRequirements &&
            typeof handleEditPhotoRequirements === 'function'
        ) {
            await handleEditPhotoRequirements(editPhotoRequirements);
        }
    };

    const handleSubmit = async () => {
        setBusy(true);

        if (editingSingleRequirement) {
            const updatedSelectedPhoto = {
                ...selectedPhoto,
                requirement: {
                    ...selectedPhoto.requirement,
                    photoTypeId: selectedTypeId,
                    requiresPullAndClean,
                },
            };

            const foundIndex = editPhotoRequirements.findIndex(
                req => req.id === currentPhotoTypeId
            );
            editPhotoRequirements[foundIndex] = {
                ...editPhotoRequirements[foundIndex],
                ...photoTypes?.find(type => type.id === selectedTypeId),
            };

            try {
                await handleEditUploadedPhotoRequirement(
                    updatedSelectedPhoto.id,
                    updatedSelectedPhoto
                );

                // OC-2049
                if (editPhotoRequirements.length > 0) {
                    await _handleEditPhotoRequirements(editPhotoRequirements);
                }

                setBusy(false);
                handleClose();
            } catch (error) {
                logger.error(error);
                Alert.error(
                    `Sorry, issue updating photo requirement: ${error.message}`
                );
            }
        } else {
            try {
                // OC-2049
                await _handleEditPhotoRequirements(editPhotoRequirements);
                setBusy(false);
                handleClose();
            } catch (error) {
                logger.error(error);
                Alert.error(
                    `Sorry, issue updating photo requirements: ${error.message}`
                );
            }
        }
    };

    const renderSelectedPreview = (selectedPhoto, fallbackMimeType) => {
        const url = selectedPhoto?.requirement?.url;
        const mime =
            selectedPhoto?.requirement?.mimeType || fallbackMimeType || '';

        if (!url) return <PreviewPlaceholder />;

        if (mime.startsWith('image/')) {
            return (
                <img
                    src={url}
                    alt={selectedPhoto?.requirement?.fileName || 'preview'}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                    }}
                />
            );
        }

        if (isVideoMimeType(mime)) {
            return (
                <VideoPreviewContainer>
                    {renderImage()}
                    <VideoMask
                        photo={selectedPhoto}
                        duration={videoDuration}
                        isShowPlayIcon={false}
                    />
                </VideoPreviewContainer>
            );
        }

        // PDF
        if (mime === 'application/pdf') {
            return (
                <PreviewContainer>
                    <PDFPreview fontSize='90px' />
                </PreviewContainer>
            );
        }
        return (
            <PreviewContainer>
                <OtherPreview fontSize='90px' />
            </PreviewContainer>
        );
    };

    const removedRequirementsDisplay = removedRequirements();

    return (
        <Drawer
            open
            onClose={handleClose}
            closeByDocument
            showClose
            drawerStyle={{ width: '45%' }}
            title={
                <span
                    style={{ fontSize: 24, fontWeight: 600, color: '#333333' }}
                >
                    {editingSingleRequirement
                        ? 'Edit Photo Type'
                        : `${
                              previousPhotoRequirements.length ? 'Edit' : 'Add'
                          } Photo Requirements`}
                </span>
            }
            subHeader=''
        >
            <DrawerBody>
                <Grid>
                    {/* ================= LEFT: PREVIEW ================= */}
                    <PreviewPane>
                        <PreviewBox>
                            {editingSingleRequirement ? (
                                <PreviewContent>
                                    {renderSelectedPreview(
                                        selectedPhoto,
                                        mimeType
                                    )}
                                </PreviewContent>
                            ) : (
                                <PreviewPlaceholder />
                            )}
                        </PreviewBox>
                    </PreviewPane>

                    {/* ================= RIGHT: FORM ================= */}
                    <FormPane>
                        {editingSingleRequirement ? (
                            <>
                                <FormSelect
                                    label={
                                        <span>
                                            Type{' '}
                                            <span
                                                style={{
                                                    color: Config.color.red,
                                                    fontStyle: 'italic',
                                                }}
                                            >
                                                (Required)
                                            </span>
                                        </span>
                                    }
                                    controlId='req-photo-label'
                                    value={selectedTypeId}
                                    options={singleRequirementDropdownOptions()}
                                    onChange={value =>
                                        handleTypeOnChange(value)
                                    }
                                    searchable
                                />

                                {[
                                    'BLOWER_WHEEL_BEFORE',
                                    'INDOOR_COIL_BEFORE',
                                ].includes(selectedTypeId) &&
                                    !mimeType?.startsWith('video/') && (
                                        <FormRow style={{ marginTop: 12 }}>
                                            <FormElement>
                                                <Checkbox
                                                    label='Requires Pull and Clean'
                                                    id='document-upload-modal-pull-and-clean-option'
                                                    value=''
                                                    name='document-upload-modal-pull-and-clean-option'
                                                    onChange={e =>
                                                        setRequiresPullAndClean(
                                                            e.target.checked
                                                        )
                                                    }
                                                    checked={
                                                        requiresPullAndClean
                                                    }
                                                    color='gray'
                                                />
                                            </FormElement>
                                        </FormRow>
                                    )}

                                {[
                                    'BLOWER_WHEEL_BEFORE',
                                    'INDOOR_COIL_BEFORE',
                                ].includes(selectedTypeId) &&
                                    !mimeType?.startsWith('video/') &&
                                    requiresPullAndClean && (
                                        <WarningWrap>
                                            <div>
                                                By selecting a Pull and Clean
                                                for this requirement, an
                                                automatic N/A photo will be
                                                applied to the After photo.
                                            </div>
                                            <div>
                                                You must tag/identify this piece
                                                of equipment and select ‘Repair’
                                                within the Equipment section of
                                                this Work Order before
                                                Submitting.
                                            </div>
                                        </WarningWrap>
                                    )}
                            </>
                        ) : (
                            <>
                                {previousPhotoRequirements.length ? (
                                    <>
                                        <FormTextView
                                            label='ADDED REQUIREMENTS'
                                            value={addedRequirements()}
                                            labelStyle={{ fontSize: 12 }}
                                            messageStyle={{ fontSize: 14 }}
                                        />
                                        <FormTextView
                                            label='REMOVED REQUIREMENTS'
                                            labelStyle={{ fontSize: 12 }}
                                            value={removedRequirementsDisplay}
                                            messageStyle={{
                                                fontSize: 14,
                                                ...(removedRequirementsDisplay !==
                                                '<NONE>'
                                                    ? {
                                                          color: Config.color
                                                              .red,
                                                      }
                                                    : {}),
                                            }}
                                        />
                                    </>
                                ) : null}

                                <FormSelect
                                    label='APPLIED PHOTO REQUIREMENTS'
                                    options={photoTypesFilter()}
                                    value={editPhotoRequirementsIsFixedFirst()}
                                    onChange={handleAddMultipleFilter}
                                    multiple
                                    searchable
                                    clearable
                                />
                            </>
                        )}
                        <ButtonsRow>
                            <MotiliButton
                                text='Cancel'
                                onClick={handleClose}
                                disabled={busy}
                                moStyle='normal'
                            />
                            <MotiliButton
                                text={
                                    editingSingleRequirement ? 'Update' : 'Save'
                                }
                                onClick={handleSubmit}
                                busy={busy}
                                disabled={
                                    editingSingleRequirement
                                        ? !singleRequirementChanged()
                                        : !requirementsChanged()
                                }
                            />
                        </ButtonsRow>
                    </FormPane>
                </Grid>
            </DrawerBody>
        </Drawer>
    );
}

EditPhotoRequirementsModal.propTypes = {
    photos: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            requirement: PropTypes.shape({
                id: PropTypes.string,
                photoTypeId: PropTypes.string,
            }),
        })
    ),
    selectedPhoto: PropTypes.shape({
        id: PropTypes.number,
        requirement: PropTypes.shape({
            id: PropTypes.string,
            photoTypeId: PropTypes.string,
            requiresPullAndClean: PropTypes.bool,
        }),
    }),
    currentPhotoTypeId: PropTypes.string,
    photoTypeRequirements: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            display: PropTypes.string,
        })
    ),
    quotePhotoTypeRequirements: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            display: PropTypes.string,
        })
    ),
    handleClose: PropTypes.func.isRequired,
    handleEditPhotoRequirements: PropTypes.func,
    handleEditUploadedPhotoRequirement: PropTypes.func,
    mimeType: PropTypes.string,
    currentRequiresPullAndClean: PropTypes.bool,
    renderImage: PropTypes.func,
    videoDuration: PropTypes.string,
};

export default EditPhotoRequirementsModal;
